<template>
    <div
        class="tpp-outer"
        role="region"
        :aria-label="t('totalProfitProgress')"
    >
        <div class="tpp-shell" :class="compact ? 'tpp-compact' : ''">

            <!-- Ambient layers -->
            <div class="tpp-ambient tpp-ambient-1" :style="`background:radial-gradient(ellipse at 0% 0%,rgba(${primaryColorRgb},0.14) 0%,transparent 55%);`"></div>
            <div class="tpp-ambient tpp-ambient-2" style="background:radial-gradient(ellipse at 100% 100%,rgba(232,121,249,0.08) 0%,transparent 50%);"></div>
            <div class="tpp-ambient tpp-ambient-3" style="background:radial-gradient(ellipse at 50% 0%,rgba(34,211,238,0.05) 0%,transparent 45%);"></div>

            <!-- ════════════════════ HERO SECTION ════════════════════ -->
            <section class="tpp-hero">
                <div class="tpp-hero-head">
                    <div class="tpp-hero-meta">
                        <span class="tpp-kicker" :style="`color:rgba(${primaryColorRgb},0.85);`">
                            <span class="tpp-kicker-dot" :style="`background:${primaryColor};box-shadow:0 0 8px ${primaryColor};`"></span>
                            Portfolio Analytics
                        </span>
                        <h2 class="tpp-hero-title">{{ t('totalProfitProgress') }}</h2>
                    </div>
                    <div
                        v-if="progressData.status !== 'no_investments'"
                        :class="`tpp-pill tpp-pill--${progressData.status}`"
                    >
                        <span class="tpp-pill-dot"></span>
                        {{ statusText }}
                    </div>
                </div>

                <!-- Empty state -->
                <div v-if="progressData.status === 'no_investments'" class="tpp-empty">
                    <div class="tpp-empty-icon" :style="`border-color:rgba(${primaryColorRgb},0.25);color:rgba(${primaryColorRgb},0.7);`">
                        <svg width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/>
                        </svg>
                    </div>
                    <p class="tpp-empty-title">{{ t('noInvestmentsYet') }}</p>
                    <p class="tpp-empty-desc">Start investing to see your profit analytics</p>
                    <Link href="/user/investments" class="tpp-empty-btn" :style="`background:linear-gradient(135deg,#7c3aed,${primaryColor});box-shadow:0 8px 24px rgba(${primaryColorRgb},0.4);`">
                        {{ t('startInvesting') }}
                        <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/></svg>
                    </Link>
                </div>

                <!-- Hero KPI: big number -->
                <template v-else>
                    <div class="tpp-kpi-wrap">
                        <div class="tpp-kpi-group">
                            <span class="tpp-kpi-currency">{{ currencySymbol }}</span>
                            <span class="tpp-kpi-value">{{ formatCurrency(displayTotalEarned) }}</span>
                        </div>
                        <div class="tpp-kpi-meta">
                            <span class="tpp-kpi-delta" :style="`background:rgba(${primaryColorRgb},0.12);color:${ringColorStart};border:1px solid rgba(${primaryColorRgb},0.25);`">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                                </svg>
                                {{ displayProgress }}%
                            </span>
                            <span class="tpp-kpi-caption">of {{ currencySymbol }}{{ formatCurrency(progressData.total_max_profit) }} max capacity</span>
                        </div>
                    </div>

                    <!-- Hero progress bar -->
                    <div class="tpp-hero-bar">
                        <div class="tpp-hero-bar-track">
                            <div
                                class="tpp-hero-bar-fill"
                                :style="`width:${Math.min(progressData.overall_progress,100)}%;background:linear-gradient(90deg,#7c3aed,${primaryColor},#e879f9);box-shadow:0 0 20px rgba(${primaryColorRgb},0.45);`"
                            >
                                <div class="tpp-shimmer"></div>
                                <div class="tpp-hero-tip" :style="`background:${primaryColor};box-shadow:0 0 10px ${primaryColor},0 0 18px rgba(${primaryColorRgb},0.5);`"></div>
                            </div>
                        </div>
                        <div class="tpp-hero-bar-labels">
                            <span>{{ currencySymbol }}0</span>
                            <span>{{ currencySymbol }}{{ formatCurrency(progressData.total_max_profit) }}</span>
                        </div>
                    </div>
                </template>
            </section>

            <template v-if="progressData.status !== 'no_investments'">

                <!-- ════════════════════ EARNINGS BREAKDOWN ════════════════════ -->
                <section class="tpp-section tpp-breakdown-section">
                    <div class="tpp-section-head">
                        <div class="tpp-section-title-wrap">
                            <div class="tpp-section-icon" :style="`background:rgba(${primaryColorRgb},0.15);color:${primaryColor};`">
                                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"/></svg>
                            </div>
                            <div>
                                <h3 class="tpp-section-title">Profit Breakdown</h3>
                                <p class="tpp-section-sub">Earnings by source</p>
                            </div>
                        </div>
                        <span class="tpp-total-chip" :style="`background:rgba(${primaryColorRgb},0.1);border-color:rgba(${primaryColorRgb},0.25);color:${primaryColor};`">
                            Total {{ currencySymbol }}{{ formatCompact(progressData.total_profit_earned) }}
                        </span>
                    </div>

                    <!-- Stacked ratio bar -->
                    <div class="tpp-ratio-bar-wrap">
                        <div class="tpp-ratio-bar">
                            <div
                                class="tpp-ratio-seg tpp-ratio-roi"
                                :style="`width:${roiContribution}%;background:linear-gradient(90deg,#7c3aed,${primaryColor});`"
                            ></div>
                            <div
                                class="tpp-ratio-seg tpp-ratio-ref"
                                :style="`width:${commissionsContribution}%;background:linear-gradient(90deg,#c026d3,#e879f9);`"
                            ></div>
                        </div>
                        <div class="tpp-ratio-labels">
                            <span :style="`color:${primaryColor};`">ROI {{ roiContribution }}%</span>
                            <span style="color:#e879f9;">Referral {{ commissionsContribution }}%</span>
                        </div>
                    </div>

                    <!-- Two source metric rows -->
                    <div class="tpp-src-list">
                        <!-- ROI -->
                        <div class="tpp-src-row" :style="`border-left-color:${primaryColor};`">
                            <div class="tpp-src-icon" :style="`background:rgba(${primaryColorRgb},0.14);`">
                                <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" :style="`color:${primaryColor};`"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/></svg>
                            </div>
                            <div class="tpp-src-body">
                                <div class="tpp-src-top">
                                    <span class="tpp-src-name">ROI Earnings</span>
                                    <span class="tpp-src-amt" :style="`color:${primaryColor};`">{{ currencySymbol }}{{ formatCurrency(progressData.total_roi_earned) }}</span>
                                </div>
                                <div class="tpp-src-bar-track">
                                    <div class="tpp-src-bar-fill" :style="`width:${roiProgressBarWidth}%;background:linear-gradient(90deg,#7c3aed,${primaryColor});`">
                                        <div class="tpp-shimmer"></div>
                                    </div>
                                </div>
                                <div class="tpp-src-foot">
                                    <span class="tpp-src-pct-badge" :style="`background:rgba(${primaryColorRgb},0.12);color:${primaryColor};`">{{ roiProgressBarDisplay }}</span>
                                    <span class="tpp-src-remain">{{ currencySymbol }}{{ formatCompact(roiRemaining) }} left</span>
                                </div>
                            </div>
                        </div>

                        <!-- Referral -->
                        <div class="tpp-src-row" style="border-left-color:#e879f9;">
                            <div class="tpp-src-icon" style="background:rgba(232,121,249,0.14);">
                                <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#e879f9;"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/></svg>
                            </div>
                            <div class="tpp-src-body">
                                <div class="tpp-src-top">
                                    <span class="tpp-src-name">Referral Commissions</span>
                                    <span class="tpp-src-amt" style="color:#e879f9;">{{ currencySymbol }}{{ formatCurrency(progressData.total_commissions_earned) }}</span>
                                </div>
                                <div class="tpp-src-bar-track">
                                    <div class="tpp-src-bar-fill" :style="`width:${commissionsProgressBarWidth}%;background:linear-gradient(90deg,#c026d3,#e879f9);`">
                                        <div class="tpp-shimmer"></div>
                                    </div>
                                </div>
                                <div class="tpp-src-foot">
                                    <span class="tpp-src-pct-badge" style="background:rgba(232,121,249,0.12);color:#e879f9;">{{ commissionsProgressBarDisplay }}</span>
                                    <span class="tpp-src-remain">{{ currencySymbol }}{{ formatCompact(commissionsRemaining) }} left</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- ════════════════════ QUICK STATS ════════════════════ -->
                <section class="tpp-quick-stats">
                    <div class="tpp-qs" :style="`border-color:rgba(${primaryColorRgb},0.2);background:rgba(${primaryColorRgb},0.05);`">
                        <div class="tpp-qs-top">
                            <div class="tpp-qs-icon" :style="`background:rgba(${primaryColorRgb},0.14);`">
                                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" :style="`color:${primaryColor};`"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <span class="tpp-qs-label">{{ t('maxProfit') }}</span>
                        </div>
                        <span class="tpp-qs-val" style="color:#fff;">{{ currencySymbol }}{{ formatCompact(progressData.total_max_profit) }}</span>
                    </div>

                    <div class="tpp-qs" style="border-color:rgba(16,185,129,0.22);background:rgba(16,185,129,0.05);">
                        <div class="tpp-qs-top">
                            <div class="tpp-qs-icon" style="background:rgba(16,185,129,0.14);">
                                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#10b981;"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <span class="tpp-qs-label">{{ t('totalEarned') }}</span>
                        </div>
                        <span class="tpp-qs-val" style="color:#10b981;">{{ currencySymbol }}{{ formatCompact(progressData.total_profit_earned) }}</span>
                    </div>

                    <div class="tpp-qs" style="border-color:rgba(34,211,238,0.22);background:rgba(34,211,238,0.05);">
                        <div class="tpp-qs-top">
                            <div class="tpp-qs-icon" style="background:rgba(34,211,238,0.14);">
                                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#22d3ee;"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <span class="tpp-qs-label">{{ t('remaining') }}</span>
                        </div>
                        <span class="tpp-qs-val" style="color:#fff;">{{ currencySymbol }}{{ formatCompact(progressData.total_remaining) }}</span>
                    </div>

                    <div class="tpp-qs" style="border-color:rgba(232,121,249,0.22);background:rgba(232,121,249,0.05);">
                        <div class="tpp-qs-top">
                            <div class="tpp-qs-icon" style="background:rgba(232,121,249,0.14);">
                                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#e879f9;"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"/></svg>
                            </div>
                            <span class="tpp-qs-label">Investments</span>
                        </div>
                        <span class="tpp-qs-val" style="color:#e879f9;">{{ progressData.active_investments }}<small>/{{ progressData.active_investments + progressData.capped_investments }}</small></span>
                    </div>
                </section>

                <!-- ════════════════════ DETAILED BREAKDOWN ════════════════════ -->
                <section v-if="!compact" class="tpp-section">
                    <div class="tpp-section-head">
                        <div class="tpp-section-title-wrap">
                            <div class="tpp-section-icon" style="background:rgba(232,121,249,0.15);color:#e879f9;">
                                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"/></svg>
                            </div>
                            <div>
                                <h3 class="tpp-section-title">Profit Sources</h3>
                                <p class="tpp-section-sub">ROI vs Referral commissions</p>
                            </div>
                        </div>
                    </div>

                    <div class="tpp-breakdown">
                        <!-- ROI -->
                        <div class="tpp-bd-card" :style="`border-color:rgba(${primaryColorRgb},0.2);`">
                            <div class="tpp-bd-accent" :style="`background:linear-gradient(180deg,#7c3aed,${primaryColor},#e879f9);`"></div>
                            <div class="tpp-bd-head">
                                <div class="tpp-bd-icon" :style="`background:rgba(${primaryColorRgb},0.15);`">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" :style="`color:${primaryColor};`"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/></svg>
                                </div>
                                <div class="tpp-bd-head-txt">
                                    <span class="tpp-bd-name">{{ t('roiEarnings') }}</span>
                                    <span v-if="showContributionLabels" class="tpp-bd-sub">{{ roiContribution }}% {{ t('ofTotalEarnings') }}</span>
                                    <span v-else class="tpp-bd-sub">Primary return stream</span>
                                </div>
                                <span class="tpp-bd-badge" :style="`background:rgba(${primaryColorRgb},0.15);color:${primaryColor};border:1px solid rgba(${primaryColorRgb},0.25);`">{{ roiProgressBarDisplay }}</span>
                            </div>
                            <div class="tpp-bd-amount" style="color:#fff;">{{ currencySymbol }}{{ formatCurrency(progressData.total_roi_earned) }}</div>
                            <div class="tpp-bd-bar">
                                <div class="tpp-bd-fill" :style="`width:${roiProgressBarWidth}%;background:linear-gradient(90deg,#7c3aed,${primaryColor});`">
                                    <div class="tpp-shimmer"></div>
                                </div>
                            </div>
                            <div class="tpp-bd-foot">
                                <span>{{ t('ofMaxCapacity') }}</span>
                                <span style="color:rgba(203,213,225,0.8);font-weight:600;">{{ currencySymbol }}{{ formatCurrency(roiRemaining) }} {{ t('remaining') }}</span>
                            </div>
                        </div>

                        <!-- Referral -->
                        <div class="tpp-bd-card" style="border-color:rgba(232,121,249,0.2);">
                            <div class="tpp-bd-accent" style="background:linear-gradient(180deg,#c026d3,#e879f9,#f0abfc);"></div>
                            <div class="tpp-bd-head">
                                <div class="tpp-bd-icon" style="background:rgba(232,121,249,0.15);">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#e879f9;"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/></svg>
                                </div>
                                <div class="tpp-bd-head-txt">
                                    <span class="tpp-bd-name">{{ t('referralCommissions') }}</span>
                                    <span v-if="showContributionLabels" class="tpp-bd-sub">{{ commissionsContribution }}% {{ t('ofTotalEarnings') }}</span>
                                    <span v-else class="tpp-bd-sub">Network bonus stream</span>
                                </div>
                                <span class="tpp-bd-badge" style="background:rgba(232,121,249,0.15);color:#e879f9;border:1px solid rgba(232,121,249,0.25);">{{ commissionsProgressBarDisplay }}</span>
                            </div>
                            <div class="tpp-bd-amount" style="color:#fff;">{{ currencySymbol }}{{ formatCurrency(progressData.total_commissions_earned) }}</div>
                            <div class="tpp-bd-bar">
                                <div class="tpp-bd-fill" :style="`width:${commissionsProgressBarWidth}%;background:linear-gradient(90deg,#c026d3,#e879f9);`">
                                    <div class="tpp-shimmer"></div>
                                </div>
                            </div>
                            <div class="tpp-bd-foot">
                                <span>{{ t('ofMaxCapacity') }}</span>
                                <span style="color:rgba(203,213,225,0.8);font-weight:600;">{{ currencySymbol }}{{ formatCurrency(commissionsRemaining) }} {{ t('remaining') }}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- ════════════════════ ALERTS ════════════════════ -->
                <div v-if="progressData.status === 'warning'" class="tpp-alert tpp-alert--warn">
                    <div class="tpp-alert-icon" style="background:rgba(245,158,11,0.15);color:#f59e0b;">
                        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/></svg>
                    </div>
                    <div>
                        <p class="tpp-alert-title" style="color:#f59e0b;">{{ t('profitCapWarning') }}</p>
                        <p class="tpp-alert-msg">{{ t('profitCapWarningMessage', { percentage: progressData.overall_progress.toFixed(1) }) }}</p>
                    </div>
                </div>

                <div v-else-if="progressData.status === 'capped'" class="tpp-alert tpp-alert--capped">
                    <div class="tpp-alert-icon" style="background:rgba(239,68,68,0.15);color:#ef4444;">
                        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/></svg>
                    </div>
                    <div>
                        <p class="tpp-alert-title" style="color:#ef4444;">{{ t('profitCapReached') }}</p>
                        <p class="tpp-alert-msg">{{ t('profitCapReachedMessage') }}</p>
                    </div>
                </div>

            </template>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import { useTranslation } from '@/composables/useTranslation';

const props = defineProps({
    progressData: {
        type: Object,
        required: true,
        default: () => ({
            total_max_profit: 0,
            total_roi_earned: 0,
            total_commissions_earned: 0,
            total_profit_earned: 0,
            total_remaining: 0,
            overall_progress: 0,
            total_invested: 0,
            active_investments: 0,
            capped_investments: 0,
            status: 'no_investments',
            investments_breakdown: [],
        })
    },
    compact: {
        type: Boolean,
        default: false,
    }
});

const { t }          = useTranslation();
const page           = usePage();
const primaryColor   = computed(() => page.props.primaryColor || '#a75ded');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return r ? `${parseInt(r[1],16)},${parseInt(r[2],16)},${parseInt(r[3],16)}` : '167,93,237';
};

const primaryColorRgb = computed(() => hexToRgb(primaryColor.value));

const formatCurrency = (v) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const formatCompact = (v) => {
    const n = parseFloat(v) || 0;
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
    if (n >= 10_000)    return (n / 1_000).toFixed(1) + 'K';
    if (n >= 1_000)     return (n / 1_000).toFixed(2) + 'K';
    return n.toFixed(2);
};

const ringColorStart = computed(() => {
    const p = props.progressData.overall_progress;
    if (p >= 100) return '#ef4444';
    if (p >= 70)  return '#f59e0b';
    return primaryColor.value;
});

const statusText = computed(() => {
    switch (props.progressData.status) {
        case 'capped':  return t('profitCapped');
        case 'warning': return t('nearingCap');
        case 'healthy': return t('healthy');
        default:        return t('noData');
    }
});

// ── Count-up animated values ──
const displayProgress    = ref(0);
const displayTotalEarned = ref(0);

const countUp = (refObj, end, duration = 1400, decimals = 2) => {
    const target = parseFloat(end) || 0;
    if (target === 0) { refObj.value = 0; return; }
    const s = performance.now();
    const run = (now) => {
        const p = Math.min((now - s) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 4);
        refObj.value = +(target * ease).toFixed(decimals);
        if (p < 1) requestAnimationFrame(run);
        else refObj.value = target;
    };
    requestAnimationFrame(run);
};

onMounted(() => {
    setTimeout(() => {
        countUp(displayProgress,    props.progressData.overall_progress,   1500, 1);
        countUp(displayTotalEarned, props.progressData.total_profit_earned, 1600, 2);
    }, 300);
});

watch(() => props.progressData, (nd) => {
    countUp(displayProgress,    nd.overall_progress,   1200, 1);
    countUp(displayTotalEarned, nd.total_profit_earned, 1200, 2);
}, { deep: true });

// ── Contributions ──
const roiContribution = computed(() => {
    if (props.progressData.total_profit_earned <= 0) return 0;
    return Math.min(100, Math.round((props.progressData.total_roi_earned / props.progressData.total_profit_earned) * 100));
});
const commissionsContribution = computed(() => {
    if (props.progressData.total_profit_earned <= 0) return 0;
    return Math.min(100, Math.round((props.progressData.total_commissions_earned / props.progressData.total_profit_earned) * 100));
});

// ── Per-source progress of max cap ──
const roiProgressBar = computed(() => {
    if (props.progressData.total_max_profit <= 0) return 0;
    return Math.min(100, (props.progressData.total_roi_earned / props.progressData.total_max_profit) * 100);
});
const commissionsProgressBar = computed(() => {
    if (props.progressData.total_max_profit <= 0) return 0;
    return Math.min(100, (props.progressData.total_commissions_earned / props.progressData.total_max_profit) * 100);
});
const roiProgressBarWidth = computed(() => {
    const p = roiProgressBar.value;
    return (props.progressData.total_roi_earned > 0 && p < 2) ? 2 : p;
});
const commissionsProgressBarWidth = computed(() => {
    const p = commissionsProgressBar.value;
    return (props.progressData.total_commissions_earned > 0 && p < 2) ? 2 : p;
});
const roiProgressBarDisplay = computed(() => {
    const p = roiProgressBar.value;
    if (p === 0) return '0%';
    if (p < 1)  return p.toFixed(2) + '%';
    if (p < 10) return p.toFixed(1) + '%';
    return Math.round(p) + '%';
});
const commissionsProgressBarDisplay = computed(() => {
    const p = commissionsProgressBar.value;
    if (p === 0) return '0%';
    if (p < 1)  return p.toFixed(2) + '%';
    if (p < 10) return p.toFixed(1) + '%';
    return Math.round(p) + '%';
});

const roiRemaining         = computed(() => Math.max(0, props.progressData.total_max_profit - props.progressData.total_roi_earned));
const commissionsRemaining = computed(() => Math.max(0, props.progressData.total_remaining));
const showContributionLabels = computed(() =>
    props.progressData.total_roi_earned > 0 && props.progressData.total_commissions_earned > 0
);
</script>

<style scoped>
/* ═══════════════ OUTER WRAPPER ═══════════════ */
.tpp-outer {
    border-radius: 14px;
    height: 100%;
}

.tpp-shell {
    position: relative;
    overflow: hidden;
    background: rgba(10, 4, 22, 0.97);
    border: 1px solid rgba(167, 93, 237, 0.14);
    border-radius: 14px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    height: 100%;
    box-sizing: border-box;
    transition: border-color 0.25s;
}
.tpp-shell::before {
    content: '';
    position: absolute;
    top: 0; left: 15%; right: 15%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.35), transparent);
    pointer-events: none;
}
.tpp-shell:hover {
    border-color: rgba(167, 93, 237, 0.25);
}
@media (min-width: 640px) {
    .tpp-shell { padding: 16px; }
}
@media (max-width: 480px) {
    .tpp-shell { padding: 14px 12px; gap: 12px; }
}

.tpp-ambient { position: absolute; inset: 0; pointer-events: none; }

/* ═══════════════ HERO SECTION ═══════════════ */
.tpp-hero {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.tpp-hero-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.tpp-hero-meta { display: flex; flex-direction: column; gap: 4px; }

.tpp-kicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.tpp-kicker-dot {
    display: inline-block;
    width: 6px; height: 6px;
    border-radius: 50%;
    animation: ppDotBlink 2s ease-in-out infinite;
}

.tpp-hero-title {
    font-size: clamp(1.1rem, 3vw, 1.4rem);
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.025em;
    margin: 0;
    line-height: 1.2;
}

/* Status pill */
.tpp-pill {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid;
    letter-spacing: 0.04em;
    white-space: nowrap;
    backdrop-filter: blur(8px);
}
.tpp-pill--healthy { background: rgba(16,185,129,0.12); color: #10b981; border-color: rgba(16,185,129,0.3); box-shadow: 0 0 12px rgba(16,185,129,0.15), inset 0 0 8px rgba(16,185,129,0.08); }
.tpp-pill--warning { background: rgba(245,158,11,0.12); color: #f59e0b; border-color: rgba(245,158,11,0.3); box-shadow: 0 0 12px rgba(245,158,11,0.15), inset 0 0 8px rgba(245,158,11,0.08); }
.tpp-pill--capped  { background: rgba(239,68,68,0.12);  color: #ef4444; border-color: rgba(239,68,68,0.3);  box-shadow: 0 0 12px rgba(239,68,68,0.15),  inset 0 0 8px rgba(239,68,68,0.08); }

.tpp-pill-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: currentColor;
    animation: ppDotBlink 1.5s ease-in-out infinite;
}

/* Hero KPI */
.tpp-kpi-wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.tpp-kpi-group {
    display: flex;
    align-items: baseline;
    gap: 4px;
    line-height: 1;
}

.tpp-kpi-currency {
    font-size: clamp(1.3rem, 3.5vw, 1.8rem);
    font-weight: 700;
    color: rgba(148, 163, 184, 0.7);
    letter-spacing: -0.02em;
}

.tpp-kpi-value {
    font-size: clamp(2rem, 7vw, 3.2rem);
    font-weight: 900;
    color: #fff;
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
    line-height: 1;
}

.tpp-kpi-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.tpp-kpi-delta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.02em;
    font-variant-numeric: tabular-nums;
}

.tpp-kpi-caption {
    font-size: 12px;
    color: rgba(148, 163, 184, 0.7);
    font-weight: 500;
}

/* Hero bar */
.tpp-hero-bar { display: flex; flex-direction: column; gap: 6px; }

.tpp-hero-bar-track {
    position: relative;
    height: 8px;
    border-radius: 100px;
    background: rgba(255,255,255,0.05);
    overflow: visible;
}

.tpp-hero-bar-fill {
    position: relative;
    height: 100%;
    border-radius: 100px;
    overflow: hidden;
    transition: width 1.4s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 6px;
}

.tpp-hero-tip {
    position: absolute;
    right: -3px;
    top: 50%;
    transform: translateY(-50%);
    width: 12px; height: 12px;
    border-radius: 50%;
    z-index: 2;
    border: 2px solid rgba(8,3,18,1);
}

.tpp-hero-bar-labels {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: rgba(148,163,184,0.55);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

/* ═══════════════ SHARED SECTION ═══════════════ */
.tpp-section {
    position: relative;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 14px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
@media (max-width: 480px) { .tpp-section { padding: 14px 12px; border-radius: 12px; } }

.tpp-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
}

.tpp-section-title-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
}

.tpp-section-icon {
    width: 30px; height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.tpp-section-title {
    font-size: 13px;
    font-weight: 800;
    color: #fff;
    margin: 0;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.tpp-section-sub {
    font-size: 11px;
    color: rgba(148,163,184,0.6);
    margin: 2px 0 0;
    font-weight: 500;
}

/* ═══════════════ PROFIT BREAKDOWN SECTION ═══════════════ */
.tpp-breakdown-section { gap: 12px; }

.tpp-total-chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 10px;
    font-weight: 800;
    border: 1px solid;
    white-space: nowrap;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}

/* Stacked ratio bar */
.tpp-ratio-bar-wrap { display: flex; flex-direction: column; gap: 6px; }

.tpp-ratio-bar {
    display: flex;
    height: 6px;
    border-radius: 100px;
    background: rgba(255,255,255,0.05);
    overflow: hidden;
    gap: 2px;
}

.tpp-ratio-seg {
    height: 100%;
    border-radius: 100px;
    transition: width 1.4s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 0;
}

.tpp-ratio-labels {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
}

/* Source rows */
.tpp-src-list { display: flex; flex-direction: column; gap: 8px; }

.tpp-src-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    border-left: 3px solid;
    background: rgba(255,255,255,0.02);
    border-top: 1px solid rgba(255,255,255,0.04);
    border-right: 1px solid rgba(255,255,255,0.04);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.2s;
}
.tpp-src-row:hover { background: rgba(255,255,255,0.04); }

.tpp-src-icon {
    width: 28px; height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.tpp-src-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }

.tpp-src-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.tpp-src-name {
    font-size: 11px;
    font-weight: 700;
    color: rgba(255,255,255,0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tpp-src-amt {
    font-size: 13px;
    font-weight: 900;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
}

.tpp-src-bar-track {
    height: 4px;
    background: rgba(255,255,255,0.06);
    border-radius: 100px;
    overflow: hidden;
    position: relative;
}

.tpp-src-bar-fill {
    position: relative;
    height: 100%;
    border-radius: 100px;
    overflow: hidden;
    transition: width 1.4s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 3px;
}

.tpp-src-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
}

.tpp-src-pct-badge {
    font-size: 9px;
    font-weight: 800;
    padding: 2px 6px;
    border-radius: 100px;
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
}

.tpp-src-remain {
    font-size: 10px;
    color: rgba(148,163,184,0.5);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
}

/* ═══════════════ QUICK STATS ═══════════════ */
.tpp-quick-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}
@media (max-width: 640px) {
    .tpp-quick-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}

.tpp-qs {
    position: relative;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    backdrop-filter: blur(8px);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    cursor: default;
}
.tpp-qs:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(0,0,0,0.35);
}

.tpp-qs-top {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tpp-qs-icon {
    width: 26px; height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.tpp-qs-label {
    font-size: 10px;
    font-weight: 700;
    color: rgba(148,163,184,0.7);
    text-transform: uppercase;
    letter-spacing: 0.07em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tpp-qs-val {
    font-size: clamp(1rem, 2.5vw, 1.25rem);
    font-weight: 900;
    letter-spacing: -0.03em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    word-break: break-all;
}
.tpp-qs-val small {
    font-size: 0.55em;
    font-weight: 700;
    color: rgba(148,163,184,0.5);
    letter-spacing: 0;
}

/* ═══════════════ BREAKDOWN CARDS ═══════════════ */
.tpp-breakdown {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}
@media (max-width: 560px) { .tpp-breakdown { grid-template-columns: 1fr; } }

.tpp-bd-card {
    position: relative;
    padding: 14px 14px 14px 16px;
    border-radius: 14px;
    border: 1px solid;
    background: rgba(255,255,255,0.01);
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    backdrop-filter: blur(8px);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.tpp-bd-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(0,0,0,0.4);
}

.tpp-bd-accent {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    border-radius: 3px 0 0 3px;
}

.tpp-bd-head {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.tpp-bd-icon {
    width: 34px; height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.tpp-bd-head-txt {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.tpp-bd-name {
    font-size: 12px;
    font-weight: 700;
    color: rgba(255,255,255,0.92);
    letter-spacing: -0.01em;
}

.tpp-bd-sub {
    font-size: 10px;
    color: rgba(148,163,184,0.55);
    font-weight: 500;
}

.tpp-bd-badge {
    flex-shrink: 0;
    padding: 3px 8px;
    border-radius: 100px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.02em;
    font-variant-numeric: tabular-nums;
}

.tpp-bd-amount {
    font-size: clamp(1.1rem, 3vw, 1.35rem);
    font-weight: 900;
    letter-spacing: -0.03em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    word-break: break-all;
}

.tpp-bd-bar {
    position: relative;
    height: 5px;
    border-radius: 100px;
    background: rgba(255,255,255,0.06);
    overflow: hidden;
}

.tpp-bd-fill {
    position: relative;
    height: 100%;
    border-radius: 100px;
    overflow: hidden;
    transition: width 1.4s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 3px;
}

.tpp-bd-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: rgba(148,163,184,0.55);
    flex-wrap: wrap;
}

/* ═══════════════ ALERTS ═══════════════ */
.tpp-alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid;
}
.tpp-alert--warn   { background: rgba(245,158,11,0.07); border-color: rgba(245,158,11,0.22); }
.tpp-alert--capped { background: rgba(239,68,68,0.07);  border-color: rgba(239,68,68,0.22); }

.tpp-alert-icon {
    width: 30px; height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.tpp-alert-title { font-size: 12px; font-weight: 800; margin: 0 0 2px; letter-spacing: -0.01em; }
.tpp-alert-msg   { font-size: 11px; color: rgba(203,213,225,0.7); margin: 0; line-height: 1.4; }

/* ═══════════════ SHIMMER ═══════════════ */
.tpp-shimmer {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
    animation: ppShimmer 2.4s infinite;
    pointer-events: none;
}

/* ═══════════════ EMPTY STATE ═══════════════ */
.tpp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 30px 16px 8px;
    text-align: center;
}

.tpp-empty-icon {
    width: 78px; height: 78px;
    border-radius: 50%;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
}

.tpp-empty-title { font-size: 16px; font-weight: 800; color: #fff; margin: 0; letter-spacing: -0.01em; }
.tpp-empty-desc  { font-size: 12px; color: rgba(148,163,184,0.6); margin: 0; max-width: 260px; }

.tpp-empty-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 11px 22px;
    border-radius: 11px;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    text-decoration: none;
    margin-top: 8px;
    transition: opacity 0.2s, transform 0.2s;
}
.tpp-empty-btn:hover { opacity: 0.88; transform: translateY(-1px); }

/* ═══════════════ KEYFRAMES ═══════════════ */
@keyframes ppShimmer {
    0%   { transform: translateX(-150%); }
    100% { transform: translateX(150%); }
}

@keyframes ppDotBlink {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
}

/* ═══════════════ COMPACT MODE ═══════════════ */
.tpp-compact { padding: 14px; gap: 12px; }
.tpp-compact .tpp-hero { gap: 10px; }
.tpp-compact .tpp-hero-title { font-size: clamp(0.9rem, 2.5vw, 1.1rem); }
.tpp-compact .tpp-kpi-value { font-size: clamp(1.6rem, 5vw, 2.4rem); }
.tpp-compact .tpp-kpi-currency { font-size: clamp(1rem, 2.5vw, 1.3rem); }
.tpp-compact .tpp-quick-stats { grid-template-columns: repeat(2, 1fr); gap: 8px; }
.tpp-compact .tpp-qs { padding: 10px; gap: 6px; }
.tpp-compact .tpp-qs-val { font-size: clamp(0.85rem, 2vw, 1rem); }
.tpp-compact .tpp-section { padding: 12px; gap: 10px; }
.tpp-compact .tpp-hero-bar-track { height: 6px; }
.tpp-compact .tpp-kicker { font-size: 9px; }
.tpp-compact .tpp-src-amt { font-size: 12px; }
.tpp-compact .tpp-src-row { padding: 8px 10px; gap: 8px; }

/* ═══════════════ REDUCED MOTION ═══════════════ */
@media (prefers-reduced-motion: reduce) {
    .tpp-hero-bar-fill, .tpp-bd-fill { transition: none !important; }
    .tpp-shimmer, .tpp-kicker-dot, .tpp-pill-dot { animation: none !important; }
    .tpp-qs:hover, .tpp-bd-card:hover { transform: none !important; }
}

/* ═══════════════ MOBILE APP POLISH ═══════════════ */
@media (max-width: 480px) {
    .tpp-hero-head { gap: 8px; }
    .tpp-quick-stats { gap: 8px; }
    .tpp-qs { padding: 10px; }
    .tpp-qs-icon { width: 24px; height: 24px; }
    .tpp-hero-title { font-size: 1rem; }
    .tpp-kpi-value { font-size: 2.1rem; }
    .tpp-section-title { font-size: 12px; }
}
</style>
